import React, { useState, useEffect } from 'react';
import { Typography, TextField, Grid, Paper, Button, Box, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GitHubIcon from '@mui/icons-material/GitHub';
import './App.css';
import { LocationPinMap } from 'material-ui-location-pin-map';
import { useSocket, convertFileViaSocket, convertToGeoJSON } from './data';
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";

function App() {
  const SUGGESTED_URLS = [
    "https://www.mlb.com/team",
    "https://www.nwcider.com/map/"
  ];

  const [inputUrl, setInputUrl] = useState('');
  const [geojsonData, setGeojsonData] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const socket = useSocket();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    if (urlParam && socket) {
      setInputUrl(urlParam);
      processUrl(urlParam);
    }
  }, [socket]);

  const processUrl = async (url) => {
    if (!socket) {
      console.error("Socket not connected");
      return;
    }

    setIsProcessing(true);
    setProgressMessage('');

    try {
      const urlFile = new File(
        [url],
        'url.txt',
        { type: 'text/plain' }
      );

      const result = await convertFileViaSocket(
        socket,
        urlFile,
        (message) => setProgressMessage(message)
      );

      if (result.type === 'complete') {
        setGeojsonData(convertToGeoJSON(result.locations));
        setProgressMessage("");
      }
    } catch (error) {
      console.error('Error converting URL:', error);
      setProgressMessage(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const submitUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('url', inputUrl);
    window.location.href = url.toString();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(geojsonData, null, 2));
  };

  const handleSuggestionClick = (url) => {
    const baseUrl = new URL(window.location.href);
    baseUrl.searchParams.set('url', url);
    window.location.href = baseUrl.toString();
  };

  return (
    <div
      className="App"
      style={{
        paddingTop: '200px',
        position: 'relative'
      }}
    >
      <IconButton
        component="a"
        href="https://github.com/caltonji/map-maker"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          '& svg': {
            fontSize: '2rem' // Makes the icon bigger
          }
        }}
      >
        <GitHubIcon />
      </IconButton>

      <div>
        <Typography variant="h4">Convert URL to Geojson</Typography>
      </div>
      <Grid
        container
        spacing={2}
        style={{
          margin: '20px auto',
          maxWidth: '800px',
          alignItems: 'center'
        }}
      >
        <Grid item xs={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter URL"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            size="medium"
            disabled={isProcessing}
            InputProps={{
              endAdornment: isProcessing && (
                <InputAdornment position="end">
                  <CircularProgress size={24} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={submitUrl}
            size="large"
            disabled={isProcessing}
          >
            Convert to GeoJSON
          </Button>
        </Grid>
        {progressMessage && (
          <Grid item xs={12}>
            <Typography>{progressMessage}</Typography>
          </Grid>
        )}
      </Grid>
      {!inputUrl && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {SUGGESTED_URLS.map((url, index) => (
              <Button
                key={index}
                variant="outlined"
                sx={{
                  borderRadius: '20px',
                  '& .MuiButton-label': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={() => handleSuggestionClick(url)}
              >
                {url}
              </Button>
            ))}
          </Box>
        </Grid>
      )}
      {geojsonData && (
        <>
          <Box sx={{ maxWidth: 800, mx: "auto", my: 3 }}>
            <Typography variant="h6" gutterBottom align="left">
              GeoJSON Output
            </Typography>
            <Paper
              sx={{
                p: 2, // Changed p: 2 to px: 2 for horizontal padding only
                borderRadius: 2,
                overflow: "auto",
                maxHeight: 400,
                position: "relative",
                backgroundColor: "rgb(47, 47, 47)",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                sx={{
                  position: "sticky",
                  top: 8,
                  right: 8,
                  zIndex: 100,
                  float: "right"
                }}
              >
                Copy
              </Button>
              <SyntaxHighlighter
                language="json"
                style={materialDark}
                wrapLongLines
                customStyle={{
                  margin: 0, // Remove default margins from SyntaxHighlighter
                  padding: "8px 0" // Add small vertical padding
                }}
              >
                {JSON.stringify(geojsonData, null, 2)}
              </SyntaxHighlighter>
            </Paper>
          </Box>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <LocationPinMap
              data={geojsonData}
              title={"Locations"}
              accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default App;
